import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../global/theme/tokens';
import ProjectAvatar from '../../components/exo/project/ProjectAvatar';
import TaskCard from '../../components/exo/project/task-card/TaskCard';
import TaskCardList from '../../components/exo/project/task-card/TaskCardList';
import { openTaskStats } from './capacityFilter';

/**
 * One project of an employee: a slim header line with the tasks right below
 * it, so everything an employee has on their plate is visible at a glance
 * without expanding anything.
 */
const EmployeeCapacityProject = ({
  project,
  processingTaskIds = [],
  onTaskToggle,
  onTaskEdit,
  onTaskDelete,
  onTaskUsersChange,
  onTaskFilesChange,
  onTaskLinkAdd,
  onTaskLinkDelete,
  onTaskCommentSubmit,
  // tasks already filtered by the card (finished ones may be hidden)
  tasks = [],
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const stats = openTaskStats(project);

  return (
    <Box className="flex flex-col gap-1">
      {/* the header is a label, the project itself is one click away */}
      <Box className="flex items-center gap-1.5 min-w-0 px-1">
        <Tooltip title={t('Open project')}>
          <Box
            className="flex items-center gap-1.5 min-w-0 cursor-pointer"
            onClick={() => navigate(`/projects/management/${project.id}`)}
            sx={{ ':hover': { textDecoration: 'underline' } }}
          >
            <ProjectAvatar project={project} size="18px" />
            {project.nr && (
              <Typography
                fontSize="11px"
                fontWeight={600}
                className="monospace opacity-60 shrink-0"
              >
                {project.nr}
              </Typography>
            )}
            <Typography
              fontSize="12px"
              fontWeight={600}
              className="min-w-0"
              title={project.name}
              noWrap
            >
              {project.name}
            </Typography>
          </Box>
        </Tooltip>

        <Typography fontSize="10px" className="opacity-50 shrink-0" noWrap>
          {`· ${stats.open} ${t('Open')} · ${stats.points} ${t('Points')}`}
          {stats.overdue ? (
            <Box component="span" sx={{ color: colors.error }}>
              {` · ${stats.overdue} ${t('Overdue')}`}
            </Box>
          ) : null}
        </Typography>
      </Box>

      <TaskCardList>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            workPackage={task.workPackage}
            projectId={project.id}
            editmode={Boolean(project.editable)}
            isProcessing={processingTaskIds.includes(task.id)}
            onToggleComplete={onTaskToggle}
            onEdit={onTaskEdit}
            onDelete={onTaskDelete}
            onUsersChange={onTaskUsersChange}
            onFilesChange={onTaskFilesChange}
            onLinkAdd={onTaskLinkAdd}
            onLinkDelete={onTaskLinkDelete}
            onCommentSubmit={onTaskCommentSubmit}
          />
        ))}
      </TaskCardList>
    </Box>
  );
};

export default EmployeeCapacityProject;
